import { useTranslation } from 'react-i18next';

import {
  IndicatorFactType,
  IndicatorName,
  LABEL_FILTERS,
  PlotReportFactElement,
  PlotReportFactLabelNameEnum,
  SpeciesEnum,
} from '@/api/rest/resources/types/fact';
import { GraphFact } from '@/components/Charts/types';
import { NCCard, NCCard2SectionLayout, NCCardHeader } from '@/components/NCCard/NCCard';
import { SpeciesGradientExplainer } from '@/components/NCExplainers/SpeciesGradientExplainer';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray, getFactsFromFactArray } from '@/pages/shared/hooks/utils';
import { PlotCardWrapper } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotCardWrapper';
import { isNullish } from '@/utils/booleans';

import { SpeciesGradientGraphTile } from './GraphTile';
import { SpeciesGradientStatsTile } from './StatsTile';

export const SpeciesGradient = ({ species }: { species: SpeciesEnum }) => {
  const { t } = useTranslation();

  return (
    <PlotCardWrapper name={t(`shared.ncaDetail.details.speciesGradient.labels.${species}`)}>
      <SpeciesGradientCard species={species} />
    </PlotCardWrapper>
  );
};

const SpeciesGradientCard = ({ species }: { species: SpeciesEnum }) => {
  const { t } = useTranslation();

  const factNames = {
    shootDensity: IndicatorName.marine_shoot_density,
    shootDensityMin: IndicatorName.marine_shoot_density_min,
    shootDensityMax: IndicatorName.marine_shoot_density_max,
    seagrassProductivity: IndicatorName.marine_seagrass_productivity,
    seagrassProductivityMin: IndicatorName.marine_seagrass_productivity_min,
    seagrassProductivityMax: IndicatorName.marine_seagrass_productivity_max,
    nurseryHabitat: IndicatorName.marine_nursery_habitat,
    nurseryHabitatMin: IndicatorName.marine_nursery_habitat_min,
    nurseryHabitatMax: IndicatorName.marine_nursery_habitat_max,
    biomass: IndicatorName.marine_biomass,
    biomassMin: IndicatorName.marine_biomass_min,
    biomassMax: IndicatorName.marine_biomass_max,
    diversity: IndicatorName.marine_diversity,
    diversityMin: IndicatorName.marine_diversity_min,
    diversityMax: IndicatorName.marine_diversity_max,
    seaweedMeadowsBenchmark: IndicatorName.marine_seaweed_meadows_benchmark_graph,
    seaweedMeadowsBenchmarkText: IndicatorName.marine_seaweed_meadows_benchmark_text,
    speciesDensity: IndicatorName.marine_species_density,
    speciesDensityMin: IndicatorName.marine_species_density_min,
    speciesDensityMax: IndicatorName.marine_species_density_max,
    multicelularPhotosyntheticOrganisms: IndicatorName.marine_multicelular_photosynthetic_organisms,
    multicelularPhotosyntheticOrganismsMin: IndicatorName.marine_multicelular_photosynthetic_organisms_min,
    multicelularPhotosyntheticOrganismsMax: IndicatorName.marine_multicelular_photosynthetic_organisms_max,
    reproductiveCapacity: IndicatorName.marine_reproductive_capacity,
    reproductiveCapacityMin: IndicatorName.marine_reproductive_capacity_min,
    reproductiveCapacityMax: IndicatorName.marine_reproductive_capacity_max,
    urchinDensity: IndicatorName.marine_urchin_density,
    urchinDensityMin: IndicatorName.marine_urchin_density_min,
    urchinDensityMax: IndicatorName.marine_urchin_density_max,
  };

  const facts = usePlotFacts(Object.values(factNames));
  const shootDensityFacts = getSpeciesFacts(facts, factNames.shootDensity, species);
  const seagrassProductivityFacts = getSpeciesFacts(facts, factNames.seagrassProductivity, species);
  const nurseryHabitatFacts = getSpeciesFacts(facts, factNames.nurseryHabitat, species);
  const speciesDensityFacts = getSpeciesFacts(facts, factNames.speciesDensity, species);
  const multicelularPhotosyntheticOrganismsFacts = getSpeciesFacts(
    facts,
    factNames.multicelularPhotosyntheticOrganisms,
    species,
  );
  const reproductiveCapacityFacts = getSpeciesFacts(facts, factNames.reproductiveCapacity, species);
  const urchinDensityFacts = getSpeciesFacts(facts, factNames.urchinDensity, species);
  const biomassFacts = getSpeciesFactsArray(facts, factNames.biomass, species);
  const diversityFacts = getSpeciesFactsArray(facts, factNames.diversity, species);
  const meadowsBenchmarkFact = getFactFromFactArray<GraphFact[]>(facts, factNames.seaweedMeadowsBenchmark, {
    [PlotReportFactLabelNameEnum.species]: species,
    [PlotReportFactLabelNameEnum.benchmark]: LABEL_FILTERS.ALL,
  });
  const meadowsBenchmarkTextFact = getFactFromFactArray<string>(facts, factNames.seaweedMeadowsBenchmarkText, {
    [PlotReportFactLabelNameEnum.species]: species,
  });

  const hasData = [
    shootDensityFacts.value,
    seagrassProductivityFacts.value,
    nurseryHabitatFacts.value,
    speciesDensityFacts.value,
    multicelularPhotosyntheticOrganismsFacts.value,
    reproductiveCapacityFacts.value,
    urchinDensityFacts.value,
    biomassFacts.value,
    diversityFacts.value,
    meadowsBenchmarkFact?.value?.at(0)?.value,
    meadowsBenchmarkTextFact?.value,
  ].some((fact) => !isNullish(fact));

  if (!hasData) {
    return null;
  }

  return (
    <NCCard explainer={<SpeciesGradientExplainer species={species} />} data-testid={`species-gradient-${species}-nca`}>
      <NCCardHeader
        title={t(`shared.ncaDetail.details.speciesGradient.labels.${species}`)}
        className='lg:grid-cols-[1fr_1fr]'
      />
      <NCCard2SectionLayout>
        <SpeciesGradientGraphTile
          shootDensityFacts={shootDensityFacts}
          seagrassProductivityFacts={seagrassProductivityFacts}
          nurseryHabitatFacts={nurseryHabitatFacts}
          biomassFacts={biomassFacts}
          diversityFacts={diversityFacts}
          speciesDensityFacts={speciesDensityFacts}
          multicelularPhotosyntheticOrganismsFacts={multicelularPhotosyntheticOrganismsFacts}
          reproductiveCapacityFacts={reproductiveCapacityFacts}
          urchinDensityFacts={urchinDensityFacts}
          species={species}
        />
        <SpeciesGradientStatsTile
          meadowsBenchmarkFact={meadowsBenchmarkFact}
          meadowsBenchmarkTextFact={meadowsBenchmarkTextFact}
          species={species}
        />
      </NCCard2SectionLayout>
    </NCCard>
  );
};

const getSpeciesFacts = (
  facts: PlotReportFactElement<unknown>[] | undefined,
  factName: IndicatorFactType,
  species: SpeciesEnum,
): {
  min: number | null | undefined;
  max: number | null | undefined;
  value: number | null | undefined;
} => {
  return {
    min: getFactFromFactArray<number>(facts, `${factName}_min`, {
      [PlotReportFactLabelNameEnum.species]: species,
      [PlotReportFactLabelNameEnum.statistic]: 'min',
    })?.value,
    max: getFactFromFactArray<number>(facts, `${factName}_max`, {
      [PlotReportFactLabelNameEnum.species]: species,
      [PlotReportFactLabelNameEnum.statistic]: 'max',
    })?.value,
    value: getFactFromFactArray<number>(facts, factName, {
      [PlotReportFactLabelNameEnum.species]: species,
    })?.value,
  };
};

const getSpeciesFactsArray = (
  facts: PlotReportFactElement<unknown>[] | undefined,
  factName: IndicatorFactType,
  species: SpeciesEnum,
): {
  min: PlotReportFactElement<number | null>[] | undefined;
  max: PlotReportFactElement<number | null>[] | undefined;
  value: PlotReportFactElement<number | null>[] | undefined;
} => {
  return {
    min: getFactsFromFactArray<number>(facts, `${factName}_min`, {
      [PlotReportFactLabelNameEnum.species]: species,
      [PlotReportFactLabelNameEnum.functional_group]: LABEL_FILTERS.ALL,
      [PlotReportFactLabelNameEnum.statistic]: 'min',
    }),
    max: getFactsFromFactArray<number>(facts, `${factName}_max`, {
      [PlotReportFactLabelNameEnum.species]: species,
      [PlotReportFactLabelNameEnum.functional_group]: LABEL_FILTERS.ALL,
      [PlotReportFactLabelNameEnum.statistic]: 'max',
    }),
    value: getFactsFromFactArray<number>(facts, factName, {
      [PlotReportFactLabelNameEnum.species]: species,
      [PlotReportFactLabelNameEnum.functional_group]: LABEL_FILTERS.ALL,
    }),
  };
};
